import type * as Y from 'yjs';
import { parseCanvas, serializeCanvas, type CanvasModel } from './model.js';

const SAVE_DEBOUNCE_MS = 400;

/**
 * Canvas document store: one mutable model parsed from the canvas doc's
 * Y.Text JSON. All edits go through apply(fn); saves are debounced (400ms)
 * and replace the whole Y.Text in one transact (last-write-wins is the
 * accepted semantic for .canvas files). Remote changes are merged by full
 * reload, deferred while the user is mid-gesture or has unsaved local edits
 * (in which case the local save wins).
 */
export class CanvasStore {
  model: CanvasModel;
  version = 0;

  private listeners = new Set<() => void>();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private dirty = false;
  private interacting = 0;
  private pendingRemote = false;
  private destroyed = false;
  private readonly observer: (event: Y.YTextEvent, tx: Y.Transaction) => void;

  constructor(readonly ytext: Y.Text) {
    this.model = parseCanvas(ytext.toString());
    this.observer = (_event, tx) => {
      if (tx.origin === this) return; // our own write
      this.onRemote();
    };
    ytext.observe(this.observer);
  }

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  };

  getVersion = (): number => this.version;

  private bump() {
    this.version++;
    for (const cb of this.listeners) cb();
  }

  /** Single mutation entry point: mutate, re-render, debounce-save. */
  apply(fn: (m: CanvasModel) => void): void {
    if (this.destroyed) return;
    fn(this.model);
    this.dirty = true;
    this.bump();
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.saveNow(), SAVE_DEBOUNCE_MS);
  }

  /** Mark a user gesture (drag/resize/inline edit) in progress; remote
   * reloads are deferred until it ends. Re-entrant. */
  beginInteraction(): void {
    this.interacting++;
  }

  endInteraction(): void {
    this.interacting = Math.max(0, this.interacting - 1);
    if (this.interacting === 0 && this.pendingRemote && !this.dirty) this.reload();
  }

  saveNow(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    if (!this.dirty || this.destroyed) return;
    this.dirty = false;
    this.pendingRemote = false;
    const json = serializeCanvas(this.model);
    if (json === this.ytext.toString()) return;
    const write = () => {
      this.ytext.delete(0, this.ytext.length);
      this.ytext.insert(0, json);
    };
    const doc = this.ytext.doc;
    if (doc) doc.transact(write, this);
    else write();
  }

  private onRemote(): void {
    if (this.interacting > 0 || this.dirty) {
      // Mid-gesture or with pending local edits: defer; a pending local save
      // overwrites (LWW), otherwise reload once the gesture ends.
      this.pendingRemote = true;
      return;
    }
    this.reload();
  }

  private reload(): void {
    this.pendingRemote = false;
    this.model = parseCanvas(this.ytext.toString());
    this.bump();
  }

  destroy(): void {
    this.saveNow(); // flush pending edits before unmount
    this.destroyed = true;
    this.ytext.unobserve(this.observer);
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    this.listeners.clear();
  }
}
